import { BinaryDeserializer, MESSAGE_TYPES } from './binaryDeserializer';
import { OpportunityCache } from './opportunityCache';
import { getApiConfigCached } from '../config/api.config';
import { store } from '../app/store';
import { updateNetworkStatus, updateNetworkStatusBatch } from '../features/networkStatus/networkStatusSlice';

export const ConnectionStatus = {
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  DISCONNECTED: 'DISCONNECTED',
  RECONNECTING: 'RECONNECTING',
} as const;

export type ConnectionStatus = typeof ConnectionStatus[keyof typeof ConnectionStatus];

export interface WebSocketConfig {
  url: string;
  reconnectDelays: number[];
  maxReconnectDelay: number;
}

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private status: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private cache: OpportunityCache;
  private statusCallbacks: Set<(status: ConnectionStatus) => void>;
  private messageCount = 0;
  private lastMessageTime = Date.now();
  private messageStatsInterval: ReturnType<typeof setInterval> | null = null;
  private deserializer: BinaryDeserializer | null = null; // Reuse the same deserializer
  private errorCount = 0; // Track errors to detect problems
  
  constructor(config: Partial<WebSocketConfig> = {}) {
    // Get the API configuration
    const apiConfig = getApiConfigCached();

    // Determine WebSocket URL priority:
    // 1. Explicit config.url (highest priority - for testing)
    // 2. Environment variable VITE_WS_URL (for direct connections to remote backends)
    // 3. Proxy path for local development only
    // 4. Dynamic apiConfig.wsUrl for production

    const isDevelopment = window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1';

    // Check if we have an explicit WebSocket URL in environment (for remote backends)
    const envWsUrl = import.meta.env.VITE_WS_URL as string | undefined;

    let defaultUrl: string;
    if (isDevelopment) {
      // In development, check if we have a remote backend URL
      if (envWsUrl && (envWsUrl.startsWith('wss://') || envWsUrl.startsWith('ws://'))) {
        // Remote backend: connect directly (Vite proxy has issues with remote WSS)
        defaultUrl = `${envWsUrl}/ws`;
      } else {
        // Local backend: use Vite proxy
        defaultUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
      }
    } else {
      // Production: use dynamic config
      defaultUrl = apiConfig.wsUrl;
    }

    this.config = {
      url: config.url || defaultUrl,
      reconnectDelays: config.reconnectDelays || [1000, 2000, 4000, 8000, 16000],
      maxReconnectDelay: config.maxReconnectDelay || 30000,
    };

    console.log('[WebSocket] Using configuration:', {
      url: this.config.url,
      isDevelopment,
      envWsUrl,
      host: apiConfig.host,
      isProduction: apiConfig.isProduction,
      note: envWsUrl?.startsWith('wss://') ? 'Direct connection to remote backend' :
            isDevelopment ? 'Using Vite proxy for local backend' : 'Production mode',
    });
    
    this.cache = new OpportunityCache();
    this.statusCallbacks = new Set();
  }
  
  connect(): void {
    console.log('[WS Connect] Starting connection attempt');
    console.log('[WS Connect] Current state:', {
      ws: this.ws ? 'exists' : 'null',
      readyState: this.ws?.readyState,
      status: this.status
    });
    
    // Check if WebSocket constructor is available and hasn't been modified
    if (typeof WebSocket === 'undefined') {
      console.error('[WS Connect] WebSocket API is not available!');
      return;
    }
    console.log('[WS Connect] WebSocket constructor:', WebSocket.name, WebSocket.prototype.constructor.name);
    
    // Prevent multiple connection attempts
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        console.log('[WS Connect] Already connected, skipping');
        return;
      }
      if (this.ws.readyState === WebSocket.CONNECTING) {
        console.log('[WS Connect] Already connecting, skipping');
        return;
      }
      // If CLOSING or CLOSED, clean it up
      if (this.ws.readyState === WebSocket.CLOSING || this.ws.readyState === WebSocket.CLOSED) {
        console.log('[WS Connect] WebSocket in CLOSING/CLOSED state, cleaning up');
        this.ws = null;
      }
    }
    
    // Don't block if we're in RECONNECTING state (called from reconnect timer)
    if (this.status === ConnectionStatus.CONNECTING) {
      console.log('[WS Connect] Status already CONNECTING, skipping');
      return;
    }
    
    this.setStatus(ConnectionStatus.CONNECTING);
    
    try {
      console.log('[WS Connect] Creating new WebSocket to:', this.config.url);
      
      // Create new WebSocket connection
      this.ws = new WebSocket(this.config.url);
      this.ws.binaryType = 'arraybuffer'; // Critical for binary messages
      
      console.log('[WS Connect] WebSocket created, setting up handlers');
      console.log('[WS Connect] WebSocket properties:', {
        url: this.ws.url,
        readyState: this.ws.readyState,
        binaryType: this.ws.binaryType,
        protocol: this.ws.protocol,
        constructor: this.ws.constructor.name
      });
      
      // Set up event handlers - store them to prevent garbage collection
      const handlers = {
        open: (event: Event) => this.handleOpen(),
        message: (event: MessageEvent) => this.handleMessage(event),
        close: (event: CloseEvent) => this.handleClose(event),
        error: (event: Event) => this.handleError(event)
      };
      
      // Store handlers on the WebSocket to prevent GC (like the HTML version does)
      (this.ws as any).__handlers = handlers;
      
      // Attach handlers
      this.ws.onopen = handlers.open;
      this.ws.onmessage = handlers.message;
      this.ws.onclose = handlers.close;
      this.ws.onerror = handlers.error;
      
      console.log('[WS Connect] Event handlers attached');
    } catch (error) {
      console.error('[WS Connect] Failed to create WebSocket:', error);
      this.scheduleReconnect();
    }
  }
  
  disconnect(): void {
    console.log('[WS Disconnect] Disconnecting WebSocket');
    this.clearReconnectTimer();
    if (this.ws) {
      console.log('[WS Disconnect] Closing WebSocket, current state:', this.ws.readyState);
      this.ws.close();
      this.ws = null;
    }
    this.setStatus(ConnectionStatus.DISCONNECTED);
    console.log('[WS Disconnect] Disconnected');
  }
  
  private handleOpen(): void {
    console.log('[WS Open] WebSocket connection opened');
    console.log('[WS Open] WebSocket state:', {
      readyState: this.ws?.readyState,
      url: this.ws?.url,
      protocol: this.ws?.protocol,
      extensions: this.ws?.extensions
    });
    
    // Clear all old opportunities on reconnect to avoid stale data
    if (this.reconnectAttempts > 0) {
      console.log('[WS Open] This is a reconnection, clearing stale opportunities');
      this.cache.clear();
    }
    
    // Reset message tracking
    this.messageCount = 0;
    this.lastMessageTime = Date.now();
    
    // Start message stats tracking
    this.startMessageStats();
    
    // Don't send pings - the server doesn't expect them
    // The browser will handle WebSocket ping/pong frames automatically
    
    this.setStatus(ConnectionStatus.CONNECTED);
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();
    
    console.log('[WS Open] Connection fully established');
  }
  
  private startMessageStats(): void {
    // Stop any existing interval
    if (this.messageStatsInterval) {
      clearInterval(this.messageStatsInterval);
    }
    
    // Track when we started for rate calculation
    const connectionStartTime = Date.now();
    let lastReportedCount = 0;
    let lastStatsLog = Date.now();
    
    // Check connection health every 2 seconds, log stats every 10 seconds
    this.messageStatsInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastMessage = now - this.lastMessageTime;
      const connectionDuration = (now - connectionStartTime) / 1000;
      
      // Log detailed stats every 10 seconds
      if (now - lastStatsLog >= 10000) {
        const messagesInPeriod = this.messageCount - lastReportedCount;
        
        console.log('[WS Stats] Message statistics:', {
          totalMessages: this.messageCount,
          messagesInLast10s: messagesInPeriod,
          timeSinceLastMessage: `${(timeSinceLastMessage / 1000).toFixed(1)}s`,
          avgMessagesPerSecond: connectionDuration > 0 ? (this.messageCount / connectionDuration).toFixed(2) : 0,
          recentMessagesPerSecond: (messagesInPeriod / 10).toFixed(2),
          wsState: this.ws ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][this.ws.readyState] : 'null',
          bufferedAmount: this.ws?.bufferedAmount || 0
        });
        
        lastReportedCount = this.messageCount;
        lastStatsLog = now;
      }
      
      // Reconnect after 5 seconds of no messages
      // This handles the case where the connection is still "open" but messages stopped
      if (timeSinceLastMessage > 60000 && this.status === ConnectionStatus.CONNECTED) {
        console.warn('[WS Stats] ⚠️ No messages for 5 seconds, forcing reconnection');
        console.warn('[WS Stats] Connection state before reconnect:', {
          ws: this.ws ? 'exists' : 'null',
          readyState: this.ws?.readyState,
          readyStateText: this.ws ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][this.ws.readyState] : 'null',
          onmessage: this.ws?.onmessage ? 'attached' : 'null',
          totalMessages: this.messageCount,
          timeSinceLastMessage: `${(timeSinceLastMessage / 1000).toFixed(1)}s`
        });
        
        // Force reconnection to recover from stale connections
        this.forceReconnect();
      }
    }, 2000);  // Check every 2 seconds for faster reconnection
  }
  
  private stopMessageStats(): void {
    if (this.messageStatsInterval) {
      clearInterval(this.messageStatsInterval);
      this.messageStatsInterval = null;
    }
  }
  
  private handleMessage(event: MessageEvent): void {
    // Track message received (no logging per message)
    this.messageCount++;
    this.lastMessageTime = Date.now();
    
    // Log every 1000th message to see if handler is still being called
    if (this.messageCount % 1000 === 0) {
      console.log(`[WS Message] Still receiving: ${this.messageCount} messages processed`);
    }
    
    if (!(event.data instanceof ArrayBuffer)) {
      console.warn('[WS Message] Received non-binary message:', event.data);
      return;
    }
    
    try {
      const view = new DataView(event.data);
      const messageType = view.getUint8(0);
      
      // Reuse the same deserializer for performance
      if (!this.deserializer) {
        this.deserializer = new BinaryDeserializer(event.data);
      } else {
        this.deserializer.reset(event.data);
      }
      
      this.deserializer.readU8(); // Skip message type byte
      
      switch (messageType) {
        case MESSAGE_TYPES.ArbitrageOpportunity: {
          const opportunity = this.deserializer.deserializeOpportunity();
          // Process through cache - wrap in try/catch to prevent callback errors from breaking the handler
          try {
            this.cache.processOpportunity(opportunity);
            this.errorCount = 0; // Reset error count on success
          } catch (cacheError) {
            this.errorCount++;
            console.error('[WS Message] Cache processing error:', cacheError);
            if (this.errorCount > 10) {
              console.error('[WS Message] Too many cache errors, clearing cache');
              this.cache.clear();
              this.errorCount = 0;
            }
          }
          break;
        }
        
        case MESSAGE_TYPES.ArbitrageOpportunityList: {
          const opportunities = this.deserializer.deserializeOpportunityList();
          console.log(`[WS Message] Received list of ${opportunities.length} opportunities`);
          for (const opportunity of opportunities) {
            try {
              this.cache.processOpportunity(opportunity);
            } catch (cacheError) {
              console.error('[WS Message] Error processing opportunity from list:', cacheError);
            }
          }
          break;
        }
        
        case MESSAGE_TYPES.ExchangeNetworkStatus: {
          const status = this.deserializer.deserializeNetworkStatus();
          console.log(`[WS Message] Received network status for exchange ${status.exchange}:`, {
            exchange: status.exchange,
            cryptoCount: status.cryptos?.length || 0,
            timestamp: status.timestamp,
            sampleCrypto: status.cryptos?.[0]
          });
          // Dispatch to Redux store
          store.dispatch(updateNetworkStatus(status));
          break;
        }
        
        case MESSAGE_TYPES.ExchangeNetworkStatusList: {
          const statuses = this.deserializer.deserializeNetworkStatusList();
          console.log(`[WS Message] Received network status list for ${statuses.length} exchanges:`, 
            statuses.map(s => ({
              exchange: s.exchange,
              cryptoCount: s.cryptos?.length || 0
            }))
          );
          // Dispatch batch update to Redux store
          store.dispatch(updateNetworkStatusBatch(statuses));
          break;
        }
        
        default:
          // Log unknown message types
          console.warn('[WS Message] Unknown message type:', messageType);
          break;
      }
    } catch (error) {
      // Log ALL deserialization errors with details
      console.error('[WS Message] Deserialization error at message', this.messageCount, ':', error);
      console.error('[WS Message] Error details:', {
        messageSize: event.data.byteLength,
        messageType: event.data.byteLength > 0 ? new DataView(event.data).getUint8(0) : 'empty',
        error: error instanceof Error ? error.stack : error
      });
      
      // If we're getting consistent errors, something is wrong
      if (!this.deserializer) {
        console.error('[WS Message] Deserializer was null, creating new one');
      }
    }
  }
  
  private handleClose(event: CloseEvent): void {
    console.log('[WS Close] WebSocket closed');
    console.log('[WS Close] Close details:', {
      code: event.code,
      reason: event.reason || '(no reason provided)',
      wasClean: event.wasClean,
      totalMessages: this.messageCount,
      lastMessageTime: `${((Date.now() - this.lastMessageTime) / 1000).toFixed(1)}s ago`
    });
    
    // Stop message stats
    this.stopMessageStats();
    
    console.log('[WS Close] Setting ws to null');
    this.ws = null;
    
    // Clear opportunities when disconnected to avoid showing stale data
    if (this.status === ConnectionStatus.CONNECTED) {
      console.log('[WS Close] Was connected, clearing opportunities');
      this.cache.clear();
    }
    
    // If we were connected or connecting, mark as disconnected
    if (this.status === ConnectionStatus.CONNECTED || this.status === ConnectionStatus.CONNECTING) {
      console.log('[WS Close] Scheduling reconnect, previous status:', this.status);
      this.setStatus(ConnectionStatus.DISCONNECTED);
      this.scheduleReconnect();
    } else {
      console.log('[WS Close] Not scheduling reconnect, status was:', this.status);
    }
  }
  
  private handleError(event: Event): void {
    console.error('[WS Error] WebSocket error occurred');
    console.error('[WS Error] Error details:', {
      type: event.type,
      timeStamp: event.timeStamp,
      wsState: this.ws ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][this.ws.readyState] : 'null',
      status: this.status
    });
    console.error('[WS Error] Full event:', event);
  }
  
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      console.log('[WS Reconnect] Already scheduled, skipping');
      return;
    }
    
    this.setStatus(ConnectionStatus.RECONNECTING);
    
    // Calculate delay with exponential backoff
    let delay = this.config.reconnectDelays[this.reconnectAttempts] || 
                this.config.maxReconnectDelay;
    delay = Math.min(delay, this.config.maxReconnectDelay);
    
    console.log(`[WS Reconnect] Scheduling reconnection in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);
    
    this.reconnectTimer = setTimeout(() => {
      console.log('[WS Reconnect] Timer fired, attempting reconnection');
      this.reconnectTimer = null;
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }
  
  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
  
  private setStatus(status: ConnectionStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.statusCallbacks.forEach(callback => callback(status));
    }
  }
  
  onStatusChange(callback: (status: ConnectionStatus) => void): () => void {
    this.statusCallbacks.add(callback);
    callback(this.status); // Initial status
    return () => this.statusCallbacks.delete(callback);
  }
  
  getStatus(): ConnectionStatus {
    return this.status;
  }
  
  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }
  
  getCache(): OpportunityCache {
    return this.cache;
  }
  
  private forceReconnect(): void {
    console.log('[WS ForceReconnect] Forcing reconnection due to no messages');
    
    // Stop all timers
    this.stopMessageStats();
    this.clearReconnectTimer();
    
    // Close the existing connection
    if (this.ws) {
      console.log('[WS ForceReconnect] Closing existing WebSocket');
      try {
        // Remove handlers first to avoid triggering normal close logic
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.onmessage = null;
        this.ws.onopen = null;
        this.ws.close();
      } catch (e) {
        console.error('[WS ForceReconnect] Error closing WebSocket:', e);
      }
      this.ws = null;
    }
    
    // Clear the cache to avoid stale data
    this.cache.clear();
    
    // Reset attempts for immediate reconnection
    this.reconnectAttempts = 0;
    
    // Set status and reconnect immediately
    this.setStatus(ConnectionStatus.DISCONNECTED);
    this.scheduleReconnect();
  }
  
  destroy(): void {
    this.disconnect();
    this.cache.destroy();
    this.statusCallbacks.clear();
  }
}

// Singleton instance
console.log('[WS Module] Creating singleton instance of WebSocketManager');
export const wsManager = new WebSocketManager();

// Keep a reference on window to prevent garbage collection
if (typeof window !== 'undefined') {
  console.log('[WS Module] Attaching wsManager to window for debugging');
  (window as any).__wsManager = wsManager;
  
  // Add debugging helper
  (window as any).__wsDebug = () => {
    const ws = wsManager as any;
    console.log('=== WebSocket Debug Info ===');
    console.log('WebSocket exists:', ws.ws ? 'yes' : 'no');
    console.log('ReadyState:', ws.ws?.readyState, ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.ws?.readyState]);
    console.log('Status:', ws.status);
    console.log('Message count:', ws.messageCount);
    console.log('Last message:', ((Date.now() - ws.lastMessageTime) / 1000).toFixed(1), 'seconds ago');
    console.log('Handlers attached:', {
      onmessage: ws.ws?.onmessage ? 'yes' : 'no',
      onclose: ws.ws?.onclose ? 'yes' : 'no',
      onerror: ws.ws?.onerror ? 'yes' : 'no',
      onopen: ws.ws?.onopen ? 'yes' : 'no'
    });
    console.log('Buffered amount:', ws.ws?.bufferedAmount || 0);
    console.log('URL:', ws.ws?.url);
    console.log('===========================');
  };
  
  console.log('[WS Module] Debug helper available: window.__wsDebug()');
}